import type { FullSeafarerData } from '@/lib/types/seafarer';
import type { Certificate } from '@/lib/db';
import {
  mergeProfileImport,
  getSectionKeyFn,
  type ImportSourceType,
} from '@/lib/profile-merge';
import { buildCertificatesFromSeafarerData } from '@/lib/certificate-builder';
import { findSeaServiceOverlaps, parseDdMmYyyy, type SeaServiceOverlap } from '@/lib/sea-service-utils';
import { normalizeCertificateName } from '@/data/certificate-catalog';
import { computeProfileCompleteness, type ProfileCompleteness } from '@/lib/profile-completeness';
import type { AiDiscrepancy } from '@/lib/dg-profile/parser';

/**
 * A reviewable import plan. Computes exactly what a DG/CV import would change
 * BEFORE anything is written, so the user can confirm or cancel. The `merged`
 * (and `certificates`) returned here are the precise objects applied on confirm,
 * so the preview can never drift from the result.
 */

type ArraySection =
  | 'education'
  | 'preSeaTraining'
  | 'modularCourses'
  | 'seaGoingService'
  | 'postSeaTraining'
  | 'certificatesOfEndorsement'
  | 'certificatesOfCompetency'
  | 'authorisedDocuments';

export type ImportChangeKind = 'added' | 'updated' | 'archived' | 'unchanged';

export type ImportPlanItem = {
  kind: Exclude<ImportChangeKind, 'unchanged'>;
  title: string;
  detail: string;
};

export type ImportPlanSection = {
  section: ArraySection;
  label: string;
  added: number;
  updated: number;
  archived: number;
  unchanged: number;
  /** Added / updated / archived rows for display (unchanged rows are summarised by count only). */
  items: ImportPlanItem[];
};

export type CertConflict = {
  section: string;
  label: string;
  /** What the existing (higher-authority) record says. */
  existingValue: string;
  /** What the incoming document says — differs from existing. */
  incomingValue: string;
  note: string;
};

export type SeaServiceDateError = {
  /** Index in merged.seaGoingService */
  index: number;
  shipName: string;
  serviceFrom: string;
  serviceTo: string;
  errorType: 'reversed_dates' | 'future_date' | 'unparseable_date';
  note: string;
};

export type ImportPlan = {
  /** The merged profile to persist on confirm. */
  merged: FullSeafarerData;
  /** The certificate list rebuilt from `merged`, ready to store on confirm. */
  certificates: Certificate[];
  sections: ImportPlanSection[];
  totals: { added: number; updated: number; archived: number; unchanged: number };
  certificatesBefore: number;
  certificatesAfter: number;
  isFirstImport: boolean;
  /**
   * Sea-service entries in the merged profile whose date ranges overlap.
   * A seafarer cannot sail on two ships simultaneously; these are likely
   * data errors in the source document and should be flagged to the user.
   */
  seaServiceOverlaps: SeaServiceOverlap[];
  /**
   * Cert number / date mismatches detected between existing and incoming records
   * for the same canonical certificate family.
   */
  certConflicts: CertConflict[];
  /** Discrepancies flagged by the AI parser when given existing-profile context. */
  aiDiscrepancies: AiDiscrepancy[];
  /** Rank-based profile completeness computed from the merged profile. */
  completeness: ProfileCompleteness | null;
  /**
   * Sea-service records with data quality problems: reversed sign-on/off dates,
   * sign-off dates more than 2 years in the future, or unparseable dates.
   */
  seaServiceDateErrors: SeaServiceDateError[];
};

export type { SeaServiceOverlap, AiDiscrepancy, ProfileCompleteness };

const SECTION_META: Array<{
  key: ArraySection;
  label: string;
  title: (record: any) => string;
  detail: (record: any) => string;
}> = [
  {
    key: 'modularCourses',
    label: 'STCW Courses',
    title: r => r.courseName || 'Course',
    detail: r => [r.certificateNo, r.dateOfIssue].filter(Boolean).join(' · '),
  },
  {
    key: 'certificatesOfCompetency',
    label: 'Certificates of Competency',
    title: r => r.grade || 'Certificate of Competency',
    detail: r => [r.certificateNumber, r.issueDate].filter(Boolean).join(' · '),
  },
  {
    key: 'certificatesOfEndorsement',
    label: 'Endorsements',
    title: r => r.endorsementType || 'Endorsement',
    detail: r => [r.tankerType, r.dateOfIssue].filter(Boolean).join(' · '),
  },
  {
    key: 'authorisedDocuments',
    label: 'Documents',
    title: r => r.documentType || 'Document',
    detail: r => [r.documentNumber, r.issuedDate].filter(Boolean).join(' · '),
  },
  {
    key: 'seaGoingService',
    label: 'Sea Service',
    title: r => r.shipName || 'Vessel',
    detail: r => [r.rank, [r.serviceFrom, r.serviceTo].filter(Boolean).join('–')].filter(Boolean).join(' · '),
  },
  {
    key: 'education',
    label: 'Education',
    title: r => r.educationLevel || r.instituteName || 'Education',
    detail: r => [r.instituteName, r.yearOfPassing].filter(Boolean).join(' · '),
  },
  {
    key: 'preSeaTraining',
    label: 'Pre-sea Training',
    title: r => r.courseName || 'Training',
    detail: r => [r.certificateNo, r.attendedTo].filter(Boolean).join(' · '),
  },
  {
    key: 'postSeaTraining',
    label: 'Post-sea Training',
    title: r => r.courseName || r.grade || 'Training',
    detail: r => [r.certificateNo, r.attendedTo].filter(Boolean).join(' · '),
  },
];

const MAX_ITEMS_PER_SECTION = 8;

function withoutId(record: Record<string, unknown>): Record<string, unknown> {
  const { _recordId, ...rest } = record;
  return rest;
}

function contentChanged(before: Record<string, unknown>, after: Record<string, unknown>): boolean {
  const a = withoutId(before);
  const b = withoutId(after);
  const keys = Array.from(new Set([...Object.keys(a), ...Object.keys(b)])).sort();
  return keys.some(key => JSON.stringify(a[key] ?? null) !== JSON.stringify(b[key] ?? null));
}

function isHistorical(data: FullSeafarerData | null, recordId?: string): boolean {
  return !!(recordId && data?._provenanceMap?.[recordId]?.isHistorical);
}

function classifySection(
  existing: FullSeafarerData | null,
  merged: FullSeafarerData,
  meta: (typeof SECTION_META)[number],
): ImportPlanSection {
  const existingArr = ((existing?.[meta.key] as any[]) ?? []);
  const mergedArr = ((merged[meta.key] as any[]) ?? []);

  const byId = new Map<string, any>();
  const byKey = new Map<string, any[]>();
  const keyFn = getSectionKeyFn(meta.key);
  for (const record of existingArr) {
    if (record._recordId) byId.set(record._recordId, record);
    const key = keyFn(record);
    byKey.set(key, [...(byKey.get(key) ?? []), record]);
  }
  const claimedKeys = new Map<string, number>();

  const section: ImportPlanSection = {
    section: meta.key,
    label: meta.label,
    added: 0,
    updated: 0,
    archived: 0,
    unchanged: 0,
    items: [],
  };

  for (const record of mergedArr) {
    let prior = record._recordId ? byId.get(record._recordId) : undefined;
    if (!prior) {
      // Legacy / no recordId: fall back to content-key match, consuming one per key.
      const key = keyFn(record);
      const queue = byKey.get(key);
      const used = claimedKeys.get(key) ?? 0;
      if (queue && used < queue.length) {
        prior = queue[used];
        claimedKeys.set(key, used + 1);
      }
    }

    const nowHistorical = isHistorical(merged, record._recordId);
    const item = { title: meta.title(record), detail: meta.detail(record) };

    if (!prior) {
      if (nowHistorical) continue; // newly added but already historical — not user-relevant
      section.added += 1;
      if (section.items.length < MAX_ITEMS_PER_SECTION) section.items.push({ kind: 'added', ...item });
      continue;
    }

    const wasHistorical = isHistorical(existing, prior._recordId);
    if (nowHistorical && !wasHistorical) {
      section.archived += 1;
      if (section.items.length < MAX_ITEMS_PER_SECTION) section.items.push({ kind: 'archived', ...item });
    } else if (nowHistorical && wasHistorical) {
      // already archived before this import — no change to report
    } else if (contentChanged(prior, record)) {
      section.updated += 1;
      if (section.items.length < MAX_ITEMS_PER_SECTION) section.items.push({ kind: 'updated', ...item });
    } else {
      section.unchanged += 1;
    }
  }

  return section;
}

function normCertNo(v: string | undefined | null): string {
  return (v ?? '').trim().toUpperCase().replace(/[\s\-/]/g, '');
}

function meaningful(v: string | undefined | null): boolean {
  const s = (v ?? '').trim().toLowerCase();
  return !!s && !['n/a', 'na', 'nil', 'none', 'unknown', '-', ''].includes(s);
}

/**
 * Detect cert-number mismatches between the existing profile and what the
 * incoming document claims for the SAME canonical certificate family.
 * E.g. existing CDC "MUM/01/12345" vs incoming "MUM/01/99999" → report conflict.
 */
function detectCertConflicts(
  existing: FullSeafarerData | null,
  incoming: FullSeafarerData,
): CertConflict[] {
  if (!existing) return [];
  const conflicts: CertConflict[] = [];

  // Helper: build canonicalId → { number, name } map for a section
  type CertEntry = { certNo: string; name: string };

  function mapCourses(src: FullSeafarerData): Map<string, CertEntry> {
    const m = new Map<string, CertEntry>();
    for (const c of src.modularCourses ?? []) {
      const match = normalizeCertificateName(c.courseName);
      if (!match) continue;
      if (!m.has(match.canonicalId) && meaningful(c.certificateNo)) {
        m.set(match.canonicalId, { certNo: normCertNo(c.certificateNo), name: c.courseName });
      }
    }
    return m;
  }

  function mapDocs(src: FullSeafarerData): Map<string, CertEntry> {
    const m = new Map<string, CertEntry>();
    for (const d of src.authorisedDocuments ?? []) {
      const key = (d.documentType ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
      if (key && meaningful(d.documentNumber)) {
        m.set(key, { certNo: normCertNo(d.documentNumber), name: d.documentType });
      }
    }
    return m;
  }

  function mapCocs(src: FullSeafarerData): Map<string, CertEntry> {
    const m = new Map<string, CertEntry>();
    for (const c of src.certificatesOfCompetency ?? []) {
      const key = (c.grade ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
      if (key && meaningful(c.certificateNumber)) {
        m.set(key, { certNo: normCertNo(c.certificateNumber), name: c.grade });
      }
    }
    return m;
  }

  // Compare STCW courses
  const existingCourses = mapCourses(existing);
  const incomingCourses = mapCourses(incoming);
  for (const [canonicalId, existEntry] of existingCourses) {
    const inEntry = incomingCourses.get(canonicalId);
    if (!inEntry || !meaningful(inEntry.certNo)) continue;
    if (existEntry.certNo !== inEntry.certNo) {
      conflicts.push({
        section: 'STCW Course',
        label: existEntry.name,
        existingValue: existEntry.certNo,
        incomingValue: inEntry.certNo,
        note: `Certificate numbers differ for "${existEntry.name}". Existing record kept — verify which is correct.`,
      });
    }
  }

  // Compare authorised documents (CDC, Passport, etc.)
  const existingDocs = mapDocs(existing);
  const incomingDocs = mapDocs(incoming);
  for (const [key, existEntry] of existingDocs) {
    const inEntry = incomingDocs.get(key);
    if (!inEntry || !meaningful(inEntry.certNo)) continue;
    if (existEntry.certNo !== inEntry.certNo) {
      conflicts.push({
        section: 'Document',
        label: existEntry.name,
        existingValue: existEntry.certNo,
        incomingValue: inEntry.certNo,
        note: `Document numbers differ for "${existEntry.name}". Existing record kept — verify which is correct.`,
      });
    }
  }

  // Compare CoC
  const existingCocs = mapCocs(existing);
  const incomingCocs = mapCocs(incoming);
  for (const [key, existEntry] of existingCocs) {
    const inEntry = incomingCocs.get(key);
    if (!inEntry || !meaningful(inEntry.certNo)) continue;
    if (existEntry.certNo !== inEntry.certNo) {
      conflicts.push({
        section: 'CoC',
        label: existEntry.name,
        existingValue: existEntry.certNo,
        incomingValue: inEntry.certNo,
        note: `CoC certificate numbers differ for "${existEntry.name}". This may indicate a renewal — check both records.`,
      });
    }
  }

  return conflicts;
}

const TWO_YEARS_MS = 2 * 365.25 * 24 * 60 * 60 * 1000;

function detectSeaServiceDateErrors(merged: FullSeafarerData): SeaServiceDateError[] {
  const errors: SeaServiceDateError[] = [];
  const now = Date.now();

  (merged.seaGoingService ?? []).forEach((s, index) => {
    const shipName = s.shipName || `Service #${index + 1}`;
    const from = parseDdMmYyyy(s.serviceFrom);
    const to = s.serviceTo ? parseDdMmYyyy(s.serviceTo) : null;

    if (s.serviceFrom && from === null) {
      errors.push({ index, shipName, serviceFrom: s.serviceFrom, serviceTo: s.serviceTo ?? '', errorType: 'unparseable_date', note: `Sign-on date "${s.serviceFrom}" could not be parsed as DD/MM/YYYY.` });
      return;
    }
    if (s.serviceTo && to === null) {
      errors.push({ index, shipName, serviceFrom: s.serviceFrom ?? '', serviceTo: s.serviceTo, errorType: 'unparseable_date', note: `Sign-off date "${s.serviceTo}" could not be parsed as DD/MM/YYYY.` });
      return;
    }
    if (from !== null && to !== null && to < from) {
      errors.push({ index, shipName, serviceFrom: s.serviceFrom ?? '', serviceTo: s.serviceTo ?? '', errorType: 'reversed_dates', note: `Sign-off (${s.serviceTo}) is before sign-on (${s.serviceFrom}). Dates may be swapped.` });
      return;
    }
    if (to !== null && to > now + TWO_YEARS_MS) {
      errors.push({ index, shipName, serviceFrom: s.serviceFrom ?? '', serviceTo: s.serviceTo ?? '', errorType: 'future_date', note: `Sign-off date ${s.serviceTo} is more than 2 years in the future — likely a data entry error.` });
    }
  });

  return errors;
}

export function buildImportPlan(
  existing: FullSeafarerData | null,
  incoming: FullSeafarerData,
  sourceType: ImportSourceType = 'dg-profile',
  now = new Date().toISOString(),
  aiDiscrepancies: AiDiscrepancy[] = [],
): ImportPlan {
  const merged = mergeProfileImport(existing, incoming, now, sourceType);
  const certificates = buildCertificatesFromSeafarerData(merged);
  const certificatesBefore = existing ? buildCertificatesFromSeafarerData(existing).length : 0;

  const sections = SECTION_META
    .map(meta => classifySection(existing, merged, meta))
    .filter(section => section.added + section.updated + section.archived + section.unchanged > 0);

  const totals = sections.reduce(
    (acc, section) => ({
      added: acc.added + section.added,
      updated: acc.updated + section.updated,
      archived: acc.archived + section.archived,
      unchanged: acc.unchanged + section.unchanged,
    }),
    { added: 0, updated: 0, archived: 0, unchanged: 0 },
  );

  const seaServiceOverlaps = findSeaServiceOverlaps(merged.seaGoingService ?? []);
  const certConflicts = detectCertConflicts(existing, incoming);
  const completeness = computeProfileCompleteness(merged);
  const seaServiceDateErrors = detectSeaServiceDateErrors(merged);

  return {
    merged,
    certificates,
    sections,
    totals,
    certificatesBefore,
    certificatesAfter: certificates.length,
    isFirstImport: !existing,
    seaServiceOverlaps,
    certConflicts,
    aiDiscrepancies,
    completeness,
    seaServiceDateErrors,
  };
}
